import { useCallback, useEffect, useState } from "react";
import type { SourceImage, SourceImagesResponse } from "@edu/shared";
import { hasFile, roomLeft, type MaterialThumb } from "./material.ts";
import { useAssetUrl } from "../../media/asset-url.ts";
import type { MaterialState } from "./use-material.ts";

/**
 * Картинки, которые можно взять в материал из готового: доска урока (1.2,
 * источник 2) и заготовка (источник 3) `docs/homework-scratch-design.md`.
 *
 * ⚠️ **Флажки по умолчанию СНЯТЫ**: там лежит то, что преподаватель готовил для
 * себя и для урока (включая построенный график ответа), и отправлять это
 * ученику без его действия нельзя.
 */

export interface SourceImagesView {
  images: SourceImage[] | null;
  failed: boolean;
  reload: () => void;
}

/** Лента по ключу. `null` означает «ленты нет», запроса тоже нет. */
export function useSourceImages(
  key: string | null,
  load: (key: string) => Promise<SourceImagesResponse>,
): SourceImagesView {
  const [images, setImages] = useState<SourceImage[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setImages(null);
    setFailed(false);
    if (key === null) return;
    let alive = true;
    load(key)
      .then((res) => {
        if (alive) setImages(res.images);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [key, load, attempt]);

  return { images, failed, reload: useCallback(() => setAttempt((n) => n + 1), []) };
}

function thumbOf(image: SourceImage): MaterialThumb {
  return { kind: "path", path: image.previewPath ?? image.path };
}

/**
 * «С доски урока». Первыми брошенные на доску, фоны слайдов ниже свёрнутым
 * блоком: у импортированного учебника это десятки страниц, и среди них
 * страницы ответов.
 */
export function LessonImages(props: { view: SourceImagesView; material: MaterialState }) {
  const { images, failed } = props.view;
  if (failed) return <LoadFailed onRetry={props.view.reload} what="Картинки урока" />;
  if (images === null) return null;
  const board = images.filter((i) => i.source === "board");
  const slides = images.filter((i) => i.source === "slide");
  return (
    <div className="hwmat__source">
      <h3>С доски урока</h3>
      {images.length === 0 && <p className="muted">На доске этого урока картинок нет.</p>}
      {board.length > 0 && <ImageChecks images={board} material={props.material} />}
      {slides.length > 0 && (
        <details className="hwmat__slides">
          <summary>Фоны слайдов ({slides.length})</summary>
          <ImageChecks images={slides} material={props.material} />
        </details>
      )}
    </div>
  );
}

/** Картинки выбранной заготовки, рядом с её заданиями. Есть «Отметить все». */
export function PlanImages(props: { view: SourceImagesView; material: MaterialState }) {
  const { images, failed } = props.view;
  if (failed) return <LoadFailed onRetry={props.view.reload} what="Картинки заготовки" />;
  if (images === null || images.length === 0) return null;
  const all = images.every((i) => hasFile(props.material.items, i.fileId));
  return (
    <div className="hwmat__source">
      <h3>Картинки заготовки</h3>
      <ImageChecks images={images} material={props.material} />
      {!all && (
        <p className="acts">
          <button
            type="button"
            className="btn btn--quiet"
            disabled={roomLeft(props.material.items) === 0}
            onClick={() =>
              props.material.addAll(images.map((i) => ({ fileId: i.fileId, thumb: thumbOf(i) })))
            }
          >
            Отметить все
          </button>
        </p>
      )}
    </div>
  );
}

function LoadFailed(props: { what: string; onRetry: () => void }) {
  return (
    <p className="muted">
      {props.what} не загрузились.{" "}
      <button type="button" className="btn btn--quiet" onClick={props.onRetry}>
        Повторить
      </button>
    </p>
  );
}

function ImageChecks(props: { images: SourceImage[]; material: MaterialState }) {
  const full = roomLeft(props.material.items) === 0;
  return (
    <ul className="hwmat__checks">
      {props.images.map((image) => {
        const on = hasFile(props.material.items, image.fileId);
        return (
          <li key={image.fileId}>
            <label className="hwmat__check">
              <input
                type="checkbox"
                checked={on}
                disabled={!on && full}
                onChange={() => props.material.toggle(image.fileId, thumbOf(image))}
              />
              <SourceThumb image={image} />
            </label>
          </li>
        );
      })}
    </ul>
  );
}

function SourceThumb(props: { image: SourceImage }) {
  const url = useAssetUrl(props.image.previewPath ?? props.image.path);
  if (url === null) return <span className="hwmat__thumb hwmat__thumb--empty" />;
  return <img className="hwmat__thumb" src={url} alt={props.image.name} loading="lazy" />;
}
